import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpRequest, HttpParams} from '@angular/common/http';
import * as FileSaver from 'file-saver';

import {Ticket} from '@models/ticket.model';
import {PagedResponse} from '@models/response.model';

import {TableConfig} from '@app/shared/util/table-config.utils';
import {AppUtils} from '@app/shared/util/app.utils';

import * as _ from 'lodash';

import {environment} from 'environments/environment';
import {TreeModel} from "@components/tree/tree.model";
import {Log} from "ng2-logger";
import {TicketOperation} from "@models/ticket-operation.model";
import {OperationTools} from '@app/tool/model/operation-tools.model';
import {Group} from '@app/tool/model/group.model';
import {GroupChildWrapper} from '@app/tool/model/group-child-wrapper.model';
import {Issue} from '@app/shared/models/issue.model';
import {ToolPosition} from "@app/tool/model/tool-position.model";
import {ToolReturn} from "@models/tool-return.model";
import {TicketToolReport} from "@models/ticket-tool-report.model";
import {TicketOperationStatus} from "@models/enums/ticket-operation-status.enum";
import {NextTicketOperation} from '@app/shared/models/next-ticket-operation.model';
import {map} from "rxjs/operators";

@Injectable()
export class TicketService {
    static readonly TICKETS_API_PATH = `${environment.baseURL}/api/tickets`;
    static readonly TICKETS_API_REPORT_DOWNLOAD_PATH = `${environment.baseURL}/api/tickets/download`;

    _logger = Log.create(TicketService.name);

    constructor(private http: HttpClient) {
    }

    getAllTickets(tableConfig: TableConfig = null, facilityId: number = null) {
        let params = AppUtils.buildPageableParamsFromList(tableConfig);
        if (facilityId) {
            params = params.set('facilityId', facilityId.toString());
        }
        return this.http.get<PagedResponse<Ticket>>(TicketService.TICKETS_API_PATH, {params: params})
            .pipe(map(this.mapTickets));
    }

    getTicketById(ticketId: number) {
        return this.http.get<Ticket>(`${TicketService.TICKETS_API_PATH}/${ticketId}`)
            .pipe(map(this.mapTicket));
    }

    mapTickets = res => {
        return {
            tickets: res.items.map(ticket => new Ticket(ticket)),
            count: res.total
        };
    };

    mapTicket = res => {
        const ticket = new Ticket(res);
        ticket.ticketOperations = ticket.ticketOperations.reduce((acc, operation) => {
            const existingOperation = acc.find(op => op.operationId === operation.operationId);

            if (existingOperation) {
                existingOperation.nrOfTasks += operation.nrOfTasks;
                existingOperation.status = AppUtils.computeOperationStatus([existingOperation, operation]);
                if (existingOperation.status === TicketOperationStatus.RESOLVED && existingOperation.endDate < operation.endDate) {
                    existingOperation.endDate = operation.endDate;
                }
            } else {
                acc.push({...operation} as TicketOperation);
            }

            return acc;
        }, [] as TicketOperation[]);
        return ticket;
    };

    mapTicketToolReport = res => {
        return new TicketToolReport(res);
    };


    createTicket(ticket) {
        const apiTicket = this.adaptTicket(ticket);
        this._logger.info('createTicket:: Adapted ticket to', apiTicket);
        return this.http.post<Ticket>(TicketService.TICKETS_API_PATH, apiTicket);
    }

    extractTicket(
        ticketId: number, newTicket: any, boschId: string, operationId = null) {
        const headers = new HttpHeaders().set("BoschId", boschId);
        const url = `${TicketService.TICKETS_API_PATH}/${ticketId}/extract${operationId ? '?operationId=' + operationId : ''}`;
        const ticket = this.adaptTicket(newTicket);

        return this.http.post<number>(url, ticket, {headers: headers});
    }

    pauseTicket(ticketId: number, reasonId: number, description: string, boschId: string) {
        const headers = new HttpHeaders().set("BoschId", boschId);

        return this.http.patch<Ticket>(
            `${TicketService.TICKETS_API_PATH}/${ticketId}/pause`,
            {reasonId: reasonId, description: description}, {headers: headers}
        );
    }

    resumeTicket(ticketId: number, boschId: string) {
        const headers = new HttpHeaders().set("BoschId", boschId);

        return this.http.patch<Ticket>(
            `${TicketService.TICKETS_API_PATH}/${ticketId}/resume`, null, {headers: headers}
        );
    }


    returnTools(ticketId: number, operationId: number, ticketOperationsId: number, boschId: any) {
        const headers = new HttpHeaders().set("BoschId", boschId);

        return this.http.post<Ticket>(
            `${TicketService.TICKETS_API_PATH}/${ticketId}/return-tools`, {
                operationId: operationId,
                ticketOperationsId: ticketOperationsId
            }, {headers: headers});
    }

    createTicketFromReturn(ticket, returnId) {
        const apiTicket = this.adaptTicket(ticket);
        this._logger.info('createTicket:: Adapted ticket to', apiTicket);
        return this.http.post<Ticket>(`${TicketService.TICKETS_API_PATH}/fromReturn/${returnId}`, apiTicket);
    }

    checkForSimilarTickets(ticket) {
        const apiTicket = this.adaptTicket(ticket);
        this._logger.info('checkForSimilarTickets::', apiTicket);
        return this.http.post<Ticket>(`${TicketService.TICKETS_API_PATH}/checkForSimilarTickets`, apiTicket);
    }

    requestTools(ticket, boschId) {
        const headers = new HttpHeaders().set("BoschId", boschId);
        return this.http.post<Ticket>(`${TicketService.TICKETS_API_PATH}/${ticket.id}/requestTools`, null, {headers: headers});
    }

    closeTicket(ticket, boschId) {
        const headers = new HttpHeaders().set("BoschId", boschId);
        return this.http.patch<Ticket>(`${TicketService.TICKETS_API_PATH}/${ticket.id}/close`, null, {headers: headers});
    }

    startOperation(ticket, operationId, boschId) {
        const headers = new HttpHeaders().set("BoschId", boschId);
        const url = `${TicketService.TICKETS_API_PATH}/${ticket.id}/startOperation?operationId=${operationId}`;
        return this.http.patch<TicketOperation[]>(url, null, {headers: headers});
    }

    getTicketProductionData(ticketId, operationId) {
        return this.http.get<any>(`${TicketService.TICKETS_API_PATH}/${ticketId}/production-data?operationId=${operationId}`);
    }

    finishOperations(ticketId, operationId, operationDescription, operationFile, boschId) {
        const headers = new HttpHeaders().set("BoschId", boschId);
        const url = `${TicketService.TICKETS_API_PATH}/${ticketId}/finishOperations?operationId=${operationId}`;

        const formData = new FormData();
        if (operationDescription) {
            formData.append("comment", operationDescription);
        }
        if (operationFile) {
            formData.append("file", operationFile);
        }

        return this.http.patch<NextTicketOperation[]>(url, formData, { headers });
    }

    generateToolTicketReport(ticket, reportData, boschId) {
        const headers = new HttpHeaders().set("BoschId", boschId);
        const url = `${TicketService.TICKETS_API_PATH}/${ticket.id}/toolReports`;

        const formData = new FormData();

        if (reportData.description) {
            formData.append('comment', reportData.description);
        }

        formData.append('affectsProduction', String(reportData.affectsProduction ?? ''));
        formData.append('criticalDeviation', String(reportData.criticalDeviation ?? ''));
        formData.append('neededBackDate', reportData.neededBackDate ?? '');
        
        if (reportData.selectedFiles && reportData.selectedFiles.length) {
            for (const file of reportData.selectedFiles) {
                formData.append('files', file);
            }
        }

        return this.http.post<TicketToolReport>(url, formData, { headers })
            .pipe(map(this.mapTicketToolReport));
    }

    updateToolPosition(ticketId: number, toolId: number, position: ToolPosition, spare: boolean) {
        return this.http.put<ToolReturn>(
            `${TicketService.TICKETS_API_PATH}/${ticketId}/tools/${toolId}/position?isSpare=${spare}`, position ? position : {});
    }

    confirmToolScan(ticketId: number, tools: TreeModel) {
        let params = new HttpParams();

        params = params.set('toolId', tools.data.id);

        const url = `${TicketService.TICKETS_API_PATH}/${ticketId}/confirmationScan`;
        return this.http.post<Ticket>(
            url,
            null, {params: params}
        );
    }

    getTicketOperationDetails(ticketId: number, operationId: number, toolId: number = null) {
        return this.http.get<any>
        (toolId ? `${TicketService.TICKETS_API_PATH}/${ticketId}/ticket-operations?operationId=${operationId}&toolId=${toolId}` :
            `${TicketService.TICKETS_API_PATH}/${ticketId}/ticket-operations?operationId=${operationId}`);
    }

    finishTasks(ticketId: any, tasks: any[], comment = null, spareQuantity = null, boschId, status, notDoneReason = null) {
        const headers = new HttpHeaders().set("BoschId", boschId);

        return this.http.patch(`${TicketService.TICKETS_API_PATH}/finishTasks`, {
            status: status === 'Resolved' ? 1 : 2,
            taskIds: tasks.map(t => t.id),
            comment: comment || '',
            spareQuantity: spareQuantity || 0,
            notDoneReason: notDoneReason || ''
        }, {headers: headers});
    }

    getTicketOperationTaskById(ticketId, ticketOperationTaskId) {
        return this.http.get<any>(`${TicketService.TICKETS_API_PATH}/${ticketId}/ticket-operation-tasks/${ticketOperationTaskId}`);
    }

    updateTicketTask(ticketId, ticketOperationTaskId, requiresObservation, requiresSpare) {
        return this.http.post<any>(`${TicketService.TICKETS_API_PATH}/${ticketId}/ticket-operation-tasks/${ticketOperationTaskId}`,
            {requiresObservation, requiresSpare})
    }

    getTicketHasTasks(ticketId) {
        return this.http.get(`${TicketService.TICKETS_API_PATH}/${ticketId}/has-tasks`);
    }

    adaptTicket(ticket) {
        return {
            description: ticket.description,
            operationIds: _.map(ticket.operationTypes, 'id'),
            ticketOperations: ticket.ticketOperations,
            hasPriority: ticket.hasPriority,
            toolIds: ticket.issueId ? _.map(ticket.selectedTools, 'id') : this.adaptTools(ticket.selectedTools),
            issueId: ticket.issueId,
            hasMaintenanceAtLine: ticket.hasMaintenanceAtLine || false,
            lineToolRequestId: ticket.lineToolRequestId,
            type: ticket.type || 0,
            lineId: ticket.lineId,
            infoFromLine: ticket.infoFromLine
        }
    }

    adaptTools(selectedTools) {
        _.remove(selectedTools, (someObj: TreeModel) => {
            return someObj.data && someObj.data.length > 1 && someObj.data.id == null;
        });

        return _.map(selectedTools, 'data.id');
    }

    buildFormDate(operationDescription, files, ticketOperationIds = null) {
        const form = new FormData();

        form.append('comment', operationDescription);

        if (ticketOperationIds) {
            for (let i = 0; i < ticketOperationIds.length; i++) {
                form.append('ticketOperationIds', ticketOperationIds[i]);
            }
        }

        if (!files) {
            return form;
        }

        for (let i = 0; i < files.length; i++) {
            form.append('file', files[i]);
        }
        return form;
    }

    downloadFile(fileToDownload: any) {
        const req = new HttpRequest('GET', fileToDownload.url, null, {responseType: 'blob'});
        this.http.request(req).subscribe(
            (data: any) => {
                if (data.body != null) {
                    FileSaver.saveAs(data.body, fileToDownload.name);
                }
            },
            (error) => {
                this._logger.error("Error downloading the file", error);
            });
    }

    downloadTicketsReport(requestConfig: TableConfig = null) {
        const params = AppUtils.buildPageableParams(requestConfig);
        return this.http.get(TicketService.TICKETS_API_REPORT_DOWNLOAD_PATH,
            {observe: 'response', params: params, responseType: 'blob'});
    }

    getOperationsForReturnOrIssueTools(toolReturnId: number, issue: Issue) {
        let params = "";
        if (issue) {
            if (issue.tools && issue.tools.length > 1) {
                params = `?issueId=${issue.id}`
            } else {
                params = `?toolId=${issue.tools[0].id}`
            }
        }

        return this.http.get(`${TicketService.TICKETS_API_PATH}/operationsForReturn/${toolReturnId}${params}`)
            .pipe(map(this.mapOperationTools));
    }

    mapOperationTools = res => {
        if (!res) {
            return [];
        }
        return res.map(operation => new OperationTools({
                tools: this.mapGroupableItems(operation.tools),
                operationName: operation.operationName,
                operationId: operation.operationId
            }
        ))
    };

    mapGroupableItems(items) {
        return  items.map(group => {
            if (group.subGroup) {
                return new Group(group);
            }
            return new GroupChildWrapper(group).item;
        });
    }

    approveToolReport(ticketId: number, reportId: number, status: number, description: string) {
        const url = `${TicketService.TICKETS_API_PATH}/${ticketId}/toolReports/${reportId}/status`;
        const body = { status, description };

        return this.http.patch(url, body);
    }
}
